/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * MyServiceHub Africa - Multi-Tenant Business & Marketplace Ecosystem
 * 
 * Core Architectural Flows:
 * 🚗 Customer-facing service and physical-product enquiries use the frictionless enquiry flow.
 *    Customers do not require accounts.
 *    Customer details are recorded in enquiries before WhatsApp communication.
 */

import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import React, { Suspense, lazy } from 'react';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';
import { ThemeProvider } from './components/ThemeProvider';
import { AuthProvider } from './contexts/AuthContext';
import { CountryProvider } from './contexts/CountryContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { RouteTracker, initGA } from './lib/analytics';

// Initialize GA4
initGA();
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { lazyWithRetry } from './lib/lazyLoader';

// Lazy Loaded Components with automatic retry logic for deployment stability
const Marketplace = lazyWithRetry(() => import('./components/Marketplace').then(m => ({ default: m.Marketplace })));
const LandingPage = lazyWithRetry(() => import('./components/LandingPage').then(m => ({ default: m.LandingPage })));
const Login = lazyWithRetry(() => import('./components/Login').then(m => ({ default: m.Login })));
const Signup = lazyWithRetry(() => import('./components/Signup').then(m => ({ default: m.Signup })));
const EmployeeDashboard = lazyWithRetry(() => import('./components/EmployeeDashboard').then(m => ({ default: m.EmployeeDashboard })));
const EmployeeInvite = lazyWithRetry(() => import('./components/EmployeeInvite').then(m => ({ default: m.EmployeeInvite })));
const BusinessDashboard = lazyWithRetry(() => import('./components/BusinessDashboard').then(m => ({ default: m.BusinessDashboard })));
const AdminDashboard = lazyWithRetry(() => import('./components/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const StorefrontPage = lazyWithRetry(() => import('./components/StorefrontPage').then(m => ({ default: m.StorefrontPage })));
const ListingPage = lazyWithRetry(() => import('./components/ListingPage').then(m => ({ default: m.ListingPage })));
const WaterPage = lazyWithRetry(() => import('./components/WaterPage').then(m => ({ default: m.WaterPage })));
const ProfessionalServicesPage = lazyWithRetry(() => import('./components/ProfessionalServicesPage').then(m => ({ default: m.ProfessionalServicesPage })));
const SolarPage = lazyWithRetry(() => import('./components/SolarPage').then(m => ({ default: m.SolarPage })));
const ConstructionPage = lazyWithRetry(() => import('./components/ConstructionPage').then(m => ({ default: m.ConstructionPage })));
const LaundryPage = lazyWithRetry(() => import('./components/LaundryPage').then(m => ({ default: m.LaundryPage })));
const CleaningPage = lazyWithRetry(() => import('./components/CleaningPage').then(m => ({ default: m.CleaningPage })));
const AutomotivePage = lazyWithRetry(() => import('./components/AutomotivePage').then(m => ({ default: m.AutomotivePage })));
const Terms = lazyWithRetry(() => import('./components/Terms').then(m => ({ default: m.Terms })));
const Privacy = lazyWithRetry(() => import('./components/Privacy').then(m => ({ default: m.Privacy })));
const AcceptableUse = lazyWithRetry(() => import('./components/AcceptableUse').then(m => ({ default: m.AcceptableUse })));
const Disclaimer = lazyWithRetry(() => import('./components/Disclaimer').then(m => ({ default: m.Disclaimer })));
const StartBusiness = lazyWithRetry(() => import('./components/business-builder/StartBusiness').then(m => ({ default: m.StartBusiness })));
const OpportunityDetail = lazyWithRetry(() => import('./components/business-builder/OpportunityDetail').then(m => ({ default: m.OpportunityDetail })));
const SharedLogsView = lazyWithRetry(() => import('./components/SharedLogsView').then(m => ({ default: m.SharedLogsView })));
const BlogLanding = lazyWithRetry(() => import('./components/BlogLanding').then(m => ({ default: m.BlogLanding })));
const BlogArticle = lazyWithRetry(() => import('./components/BlogArticle').then(m => ({ default: m.BlogArticle })));
const FeedbackPage = lazyWithRetry(() => import('./components/FeedbackPage').then(m => ({ default: m.FeedbackPage })));
const SourcingPage = lazyWithRetry(() => import('./components/SourcingPage').then(m => ({ default: m.SourcingPage })));
const ResetPassword = lazyWithRetry(() => import('./components/ResetPassword').then(m => ({ default: m.ResetPassword })));
const EmailChangeCallback = lazyWithRetry(() => import('./components/EmailChangeCallback').then(m => ({ default: m.EmailChangeCallback })));
const OAuthConsentPage = lazyWithRetry(() => import('./components/OAuthConsentPage').then(m => ({ default: m.OAuthConsentPage })));

const LoadingFallback = () => (
  <div className="flex items-center justify-center p-20">
    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
  </div>
);

const HomePage = () => {
  const { isTenant, loading, isStorefront } = useTenant();
  const location = useLocation();
  const [safetyCleared, setSafetyCleared] = React.useState(false);

  const hostname = window.location.hostname;
  const isRootDomain =
    hostname === 'servicehub.africa' ||
    hostname === 'www.servicehub.africa' ||
    hostname === 'myservicehub.africa' ||
    hostname === 'www.myservicehub.africa' ||
    hostname.includes('localhost') ||
    hostname.includes('gitpod.io') ||
    hostname.includes('run.app') ||
    hostname.includes('vercel.app') ||
    hostname.startsWith('ais-');

  // STABILITY RULE: Safety timeout (5s) to force clear loading if background task hangs
  React.useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        setSafetyCleared(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Main platform root path always renders LandingPage cleanly
  if (location.pathname === '/' && isRootDomain) {
    return <LandingPage />;
  }

  if (loading && !safetyCleared) return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center gap-4">
      <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      <p className="text-slate-500 font-black uppercase tracking-widest text-xs animate-pulse">
        {isStorefront ? 'Loading Storefront...' : 'Loading MyServiceHub Africa...'}
      </p>
    </div>
  );
  
  return isTenant ? <StorefrontPage /> : <LandingPage />;
};

const InventoryRedirect = () => {
  const { profile } = useAuth();
  if (profile?.role === 'admin' || profile?.role === 'platform_admin' || profile?.role === 'tenant_manager') return <Navigate to="/admin" replace />;
  return <Navigate to="/business" replace />;
};

function CanonicalSEO() {
  const location = useLocation();
  const canonicalDomain = 'https://www.myservicehub.africa';
  
  // Enforce trailing slash removal (except for /)
  let cleanPath = location.pathname;
  if (!cleanPath.startsWith('/')) {
    cleanPath = '/' + cleanPath;
  }
  if (cleanPath.length > 1 && cleanPath.endsWith('/')) {
    cleanPath = cleanPath.slice(0, -1);
  }
  
  const fullUrl = `${canonicalDomain}${cleanPath}`;

  return (
    <Helmet>
      <link rel="canonical" href={fullUrl} />
      <meta property="og:url" content={fullUrl} />
      <meta name="twitter:url" content={fullUrl} />
    </Helmet>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <CountryProvider>
            <ThemeProvider>
            <Toaster position="top-right" richColors closeButton />
            <PWAInstallPrompt />
            <Router>
              <RouteTracker />
              <CanonicalSEO />
              <TenantProvider>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {/* Auth Flow */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/register" element={<Navigate to="/signup" replace />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/auth/email-change" element={<EmailChangeCallback />} />

                  {/* Main App */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/acceptable-use" element={<AcceptableUse />} />
                  <Route path="/disclaimer" element={<Disclaimer />} />
                  <Route path="/listing/:id" element={<ListingPage />} />
                  <Route path="/business/:slug" element={<StorefrontPage />} />
                  <Route path="/feedback/:id" element={<FeedbackPage />} />
                  
                  <Route path="/marketplace" element={
                    <Layout>
                      <Marketplace />
                    </Layout>
                  } />
                  <Route path="/sourcing" element={
                    <Layout>
                      <SourcingPage />
                    </Layout>
                  } />
                  <Route path="/sourcing-hub" element={<Navigate to="/sourcing" replace />} />
                  <Route path="/business/:slug/marketplace" element={
                    <Layout>
                      <Marketplace />
                    </Layout>
                  } />

                  <Route path="/view/logs/:token" element={<SharedLogsView />} />
                  <Route path="/oauth/authorize" element={<OAuthConsentPage />} />
                  <Route path="/agent-auth/authorize" element={<OAuthConsentPage />} />

                  <Route path="/blog" element={
                    <Layout>
                      <BlogLanding />
                    </Layout>
                  } />

                  <Route path="/blog/:slug" element={
                    <Layout>
                      <BlogArticle />
                    </Layout>
                  } />

                  <Route path="/water" element={
                    <Layout>
                      <WaterPage />
                    </Layout>
                  } />

                  <Route path="/professional-services" element={
                    <Layout>
                      <ProfessionalServicesPage />
                    </Layout>
                  } />

                  <Route path="/solar" element={
                    <Layout>
                      <SolarPage />
                    </Layout>
                  } />

                  <Route path="/construction" element={
                    <Layout>
                      <ConstructionPage />
                    </Layout>
                  } />

                  <Route path="/laundry" element={
                    <Layout>
                      <LaundryPage />
                    </Layout>
                  } />

                  <Route path="/cleaning" element={
                    <Layout>
                      <CleaningPage />
                    </Layout>
                  } />

                  <Route path="/automotive" element={
                    <Layout>
                      <AutomotivePage />
                    </Layout>
                  } />
                  
                  <Route path="/start-business" element={
                    <Layout>
                      <StartBusiness />
                    </Layout>
                  } />

                  <Route path="/start-business/:id" element={
                    <Layout>
                      <OpportunityDetail />
                    </Layout>
                  } />
                  
                  <Route path="/employee" element={
                    <ProtectedRoute allowedRoles={['employee', 'platform_admin', 'admin']}>
                      <Layout>
                        <EmployeeDashboard />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/invite/employee" element={<EmployeeInvite />} />
                  
                  <Route path="/business" element={
                    <ProtectedRoute allowedRoles={['business', 'platform_admin', 'admin']}>
                      <BusinessDashboard />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/admin" element={
                    <ProtectedRoute allowedRoles={['platform_admin', 'admin', 'tenant_manager']}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/inventory" element={
                    <ProtectedRoute allowedRoles={['platform_admin', 'admin', 'business']}>
                      <InventoryRedirect />
                    </ProtectedRoute>
                  } />

                  <Route path="*" element={
                    <Layout>
                      <div className="p-8 text-center">
                        <h2 className="text-xl font-bold">404 - Page Not Found</h2>
                        <Link to="/" className="text-blue-600 mt-4 inline-block">Go back home</Link>
                      </div>
                    </Layout>
                  } />
                </Routes>
              </Suspense>
            </TenantProvider>
          </Router>
        </ThemeProvider>
      </CountryProvider>
    </AuthProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}
